import { useState, useRef, useEffect } from 'react'
import { Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'

/**
 * NDJSON event types emitted by the seed worker's reset stream.
 * Mirrors seed-sdk's `ResetEvent` union.
 */
type ResetEvent =
  | { event: 'storage_reset_start' }
  | { event: 'storage_reset_done'; files?: number; removed?: number }
  | { event: 'start'; ts: number }
  | { event: 'tables_listed'; tables: string[] }
  | { event: 'table_drop'; name: string; index: number; total: number }
  | { event: 'tables_dropped'; count: number }
  | { event: 'migrations_listed'; ids: string[] }
  | { event: 'migration_start'; id: string; index: number; total: number }
  | { event: 'migration_done'; id: string; bytes: number }
  | { event: 'done'; durationMs: number; tablesDropped: number; migrationsApplied: number }
  | { event: 'error'; phase: string; message: string }

type Phase = 'idle' | 'streaming' | 'done' | 'error'

interface LogEntry {
  kind: 'info' | 'ok' | 'error'
  text: string
}

interface Props {
  open: boolean
  shareId: number
  version: string | null
  onClose: () => void
  onComplete?: () => void
}

export function ResetProgressDialog({ open, shareId, version, onClose, onComplete }: Props) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [log, setLog] = useState<LogEntry[]>([])
  const [current, setCurrent] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ tables: { i: number; n: number }; migrations: { i: number; n: number } }>({
    tables: { i: 0, n: 0 },
    migrations: { i: 0, n: 0 },
  })
  const [elapsed, setElapsed] = useState(0)
  const [errorDetail, setErrorDetail] = useState<{ phase: string; message: string } | null>(null)
  const [summary, setSummary] = useState<{ durationMs: number; tablesDropped: number; migrationsApplied: number } | null>(null)
  const logEndRef = useRef<HTMLDivElement>(null)
  const startedAt = useRef<number>(0)

  function reset() {
    setPhase('idle')
    setLog([])
    setCurrent(null)
    setProgress({ tables: { i: 0, n: 0 }, migrations: { i: 0, n: 0 } })
    setElapsed(0)
    setErrorDetail(null)
    setSummary(null)
  }

  function appendLog(entry: LogEntry) {
    setLog(l => [...l, entry])
  }

  // Tick elapsed time while running.
  useEffect(() => {
    if (phase !== 'streaming') return
    const interval = setInterval(() => {
      setElapsed(Date.now() - startedAt.current)
    }, 200)
    return () => clearInterval(interval)
  }, [phase])

  // Auto-scroll log to bottom.
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [log])

  // Reset state on each (re-)open. Don't reset on close — the dialog needs to
  // keep showing the `done`/`error` summary during its close animation, or
  // the user briefly sees the idle Confirm button flash back (especially on
  // mobile where animations are slower).
  useEffect(() => {
    if (open) reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function runReset() {
    startedAt.current = Date.now()
    setPhase('streaming')
    setCurrent('Connecting…')

    // Version is optional — when null, the backend resolves it from the share
    // row (share.version pin) or the seed's currentVersion. One normalized
    // stream: artifacts restore → share_data refresh → DO drop + re-migrate.
    const qs = version ? `?version=${encodeURIComponent(version)}` : ''
    let res: Response
    try {
      res = await fetch(`/api/shares/${shareId}/admin/reset/stream${qs}`, { method: 'POST' })
    } catch (err) {
      setPhase('error')
      setErrorDetail({ phase: 'connect', message: err instanceof Error ? err.message : String(err) })
      return
    }
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '')
      setPhase('error')
      setErrorDetail({ phase: 'connect', message: text || `HTTP ${res.status}` })
      return
    }

    // Read NDJSON line by line
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    try {
      while (true) {
        const { value, done } = await reader.read()
        if (value) buf += decoder.decode(value, { stream: true })
        if (done) break
        let nl
        while ((nl = buf.indexOf('\n')) !== -1) {
          const line = buf.slice(0, nl).trim()
          buf = buf.slice(nl + 1)
          if (!line) continue
          let ev: ResetEvent
          try { ev = JSON.parse(line) as ResetEvent } catch { continue }
          handleEvent(ev)
        }
      }
      // Flush tail
      const tail = buf.trim()
      if (tail) {
        try { handleEvent(JSON.parse(tail) as ResetEvent) } catch { /* ignore */ }
      }
    } catch (err) {
      setPhase('error')
      setErrorDetail({ phase: 'stream-read', message: err instanceof Error ? err.message : String(err) })
    }
  }

  function handleEvent(ev: ResetEvent) {
    switch (ev.event) {
      case 'storage_reset_start':
        setCurrent('Restoring files…')
        appendLog({ kind: 'info', text: 'Restoring share files…' })
        break
      case 'storage_reset_done':
        appendLog({ kind: 'ok', text: `✓ Files restored${typeof ev.files === 'number' ? ` (${ev.files} copied${typeof ev.removed === 'number' ? `, ${ev.removed} removed` : ''})` : ''}` })
        break
      case 'start':
        break
      case 'tables_listed':
        setProgress(p => ({ ...p, tables: { i: 0, n: ev.tables.length } }))
        appendLog({ kind: 'info', text: `Dropping ${ev.tables.length} table(s)…` })
        break
      case 'table_drop':
        setCurrent(`Dropping ${ev.name} (${ev.index}/${ev.total})`)
        setProgress(p => ({ ...p, tables: { i: ev.index, n: ev.total } }))
        break
      case 'tables_dropped':
        appendLog({ kind: 'ok', text: `✓ Dropped ${ev.count} table(s)` })
        break
      case 'migrations_listed':
        setProgress(p => ({ ...p, migrations: { i: 0, n: ev.ids.length } }))
        appendLog({ kind: 'info', text: `Applying ${ev.ids.length} migration(s)…` })
        break
      case 'migration_start':
        setCurrent(`Applying ${ev.id} (${ev.index}/${ev.total})`)
        setProgress(p => ({ ...p, migrations: { i: ev.index, n: ev.total } }))
        break
      case 'migration_done':
        appendLog({ kind: 'ok', text: `✓ ${ev.id}` })
        break
      case 'done':
        setPhase('done')
        setCurrent(null)
        setSummary({
          durationMs: ev.durationMs,
          tablesDropped: ev.tablesDropped,
          migrationsApplied: ev.migrationsApplied,
        })
        onComplete?.()
        break
      case 'error':
        setPhase('error')
        setCurrent(null)
        setErrorDetail({ phase: ev.phase, message: ev.message })
        appendLog({ kind: 'error', text: `✗ Failed during ${ev.phase}: ${ev.message}` })
        break
    }
  }

  const running = phase === 'streaming'
  const totalSteps = progress.tables.n + progress.migrations.n
  const doneSteps = progress.tables.i + progress.migrations.i
  const pct = totalSteps > 0 ? Math.min(100, Math.round((doneSteps / totalSteps) * 100)) : 0

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !running) onClose() }}>
      <DialogContent className="max-w-2xl" onInteractOutside={(e) => { if (running) e.preventDefault() }}>
        <DialogHeader>
          <DialogTitle>Reset share data</DialogTitle>
          <DialogDescription>
            Drops every user-mutated row in share #{shareId}
            {version ? ` (v${version})` : ' (current version)'} and re-applies the seed's migrations from R2. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {phase === 'idle' && (
          <div className="py-4 text-sm text-muted-foreground">
            Ready to reset. Live progress will appear here once the operation starts.
          </div>
        )}

        {(running || phase === 'done' || phase === 'error') && (
          <div className="space-y-3 py-2">
            {/* Progress bar */}
            {totalSteps > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{doneSteps} / {totalSteps} steps</span>
                  <span>{(elapsed / 1000).toFixed(1)}s</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn(
                      'h-full transition-all',
                      phase === 'error' ? 'bg-destructive' : phase === 'done' ? 'bg-emerald-500' : 'bg-primary',
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Current step */}
            {current && running && (
              <div className="flex items-center gap-2 text-sm font-mono">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                <span className="text-foreground">{current}</span>
              </div>
            )}

            {/* Log */}
            <div className="max-h-[260px] overflow-y-auto rounded border bg-muted/20 p-2 text-xs font-mono space-y-0.5">
              {log.length === 0 ? (
                <span className="text-muted-foreground">Starting…</span>
              ) : (
                log.map((entry, i) => (
                  <div
                    key={i}
                    className={cn(
                      entry.kind === 'ok' && 'text-emerald-700 dark:text-emerald-400',
                      entry.kind === 'error' && 'text-destructive',
                      entry.kind === 'info' && 'text-muted-foreground',
                    )}
                  >
                    {entry.text}
                  </div>
                ))
              )}
              <div ref={logEndRef} />
            </div>

            {/* Done summary */}
            {phase === 'done' && summary && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Reset complete in {(summary.durationMs / 1000).toFixed(1)}s — dropped {summary.tablesDropped} tables, applied {summary.migrationsApplied} migrations.
                </AlertDescription>
              </Alert>
            )}

            {/* Error */}
            {phase === 'error' && errorDetail && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="font-mono text-xs whitespace-pre-wrap break-all">
                  <strong>Failed during {errorDetail.phase}:</strong> {errorDetail.message}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === 'idle' && (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button variant="destructive" onClick={runReset}>
                Confirm reset
              </Button>
            </>
          )}
          {running && (
            <Button variant="outline" disabled title="Reset still running on the server even if closed">
              Running…
            </Button>
          )}
          {(phase === 'done' || phase === 'error') && (
            <Button variant="outline" onClick={onClose}>
              <X className="h-3.5 w-3.5 mr-1" />
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
